import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import ErrorFallback from '../../components/common/ErrorFallback';
import { useLearners } from '../../services/learners';
import DepartmentSelect from '../../components/common/DepartmentSelect';
import RoleSelect from '../../components/common/RoleSelect';
import { useDebounce } from '../../hooks/useDebounce';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { Learner, LearnersQueryParams } from '../../types/learner';
import LearnerEditModal from '../../components/learners/LearnerEditModal';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { parseApiDate } from '../../utils/apiDates';
import { formatLastLogin, lastLoginTitle } from '../../utils/lastLogin';

type StatusTab = 'All' | 'Active' | 'Inactive';

const avatarColors = ['bg-violet-500', 'bg-success-500', 'bg-pink-500', 'bg-amber-500', 'bg-secondary-500'];
const getAvatarColor = (name: string) => avatarColors[name.charCodeAt(0) % avatarColors.length];
const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const tabIsActive: Record<StatusTab, boolean | undefined> = {
  All: undefined,
  Active: true,
  Inactive: false,
};

const tabBadgeColors: Record<StatusTab, string> = {
  All: 'bg-grey-800 text-white dark:bg-grey-200 dark:text-grey-900',
  Active: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  Inactive: 'bg-grey-100 text-grey-600 dark:bg-grey-800 dark:text-grey-400',
};

const LearnersListPage: React.FC = () => {
  const navigate = useNavigate();
  const { attributeFilters } = usePlanFeatures();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<StatusTab>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editLearnerId, setEditLearnerId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 350);

  const queryParams: LearnersQueryParams = {
    page,
    per_page: perPage,
    search: debouncedSearch || undefined,
    department_id: selectedDepartmentId || undefined,
    role_id: selectedRoleId || undefined,
    is_active: tabIsActive[activeTab],
    sort_by: 'first_name',
    sort_order: sortOrder,
  };

  const { data, isLoading, error, refetch } = useLearners(queryParams);

  const learners: Learner[] = data?.learners || [];
  const pagination = data?.pagination;
  const total = pagination?.total || 0;
  const totalPages = pagination?.pages || 1;

  const handleTabChange = (tab: StatusTab) => {
    setActiveTab(tab);
    setPage(1);
    setSelectedIds([]);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };



  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === learners.length && learners.length > 0 ? [] : learners.map(l => l.id));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="heading-2">Learners</h1>
        <ErrorFallback message="Failed to load learners. Please try again." onRetry={() => refetch()} />
      </div>
    );
  }

  const tabs: StatusTab[] = ['All', 'Active', 'Inactive'];
  const pageStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const pageEnd = Math.min(page * perPage, total);

  return (
    <motion.div {...slideUp} className="space-y-4">
      <PageHeader
        title="Learners"
        trail={activeTab !== 'All' ? [{ label: 'Learners', to: '/learners' }] : undefined}
        leaf={activeTab !== 'All' ? activeTab : undefined}
        actions={
          <button
            onClick={() => navigate('/learners/new')}
            className="btn-primary rounded-full inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add a New Learner
          </button>
        }
      />

      {/* Main Card */}
      <div className="card overflow-hidden">
        {/* Status Tabs */}
        <div className="border-b border-border-primary px-4">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex items-center gap-2 px-4 py-3.5 body-small font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary-600 text-primary-600 dark:border-primary-350 dark:text-primary-350'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab}
                {activeTab === tab && !isLoading && (
                  <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tabBadgeColors[tab]}`}>
                    {total}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Filters Row */}
          <div className="flex items-center gap-3">
            {/* Department + Role filters (learner attributes) — Growth+ */}
            {attributeFilters && (
              <>
                <DepartmentSelect
                  value={selectedDepartmentId}
                  onChange={(id) => {
                    setSelectedDepartmentId(id);
                    setPage(1);
                  }}
                  label="Department"
                  className="min-w-44"
                />
                <RoleSelect
                  value={selectedRoleId}
                  onChange={(id) => {
                    setSelectedRoleId(id);
                    setPage(1);
                  }}
                  label="Role"
                  className="min-w-44"
                />
              </>
            )}

            {/* Search */}
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-4 py-2.5 border border-border-primary rounded-lg bg-background-primary text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          {/* Results count */}
          {!isLoading && (
            <p className="body-small text-text-secondary">
              <span className="font-semibold text-text-primary">{total}</span> results found
            </p>
          )}

          {/* Table */}
          <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead data-shimmer-ignore>
                <tr className="bg-background-secondary">
                  <th className="py-3 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={learners.length > 0 && selectedIds.length === learners.length}
                      onChange={toggleSelectAll}
                      disabled={learners.length === 0}
                      className="rounded border-border-primary"
                    />
                  </th>
                  <th className="text-left py-3 px-4 body-small font-medium text-text-secondary">
                    <button
                      onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                      className="inline-flex items-center gap-1 hover:text-text-primary transition-colors"
                    >
                      Name
                      <svg className={`w-3.5 h-3.5 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 body-small font-medium text-text-secondary">Department</th>
                  <th className="text-left py-3 px-4 body-small font-medium text-text-secondary">Role</th>
                  <th className="text-left py-3 px-4 body-small font-medium text-text-secondary">Joined</th>
                  <th className="text-left py-3 px-4 body-small font-medium text-text-secondary">Last login</th>
                  <th className="text-left py-3 px-4 body-small font-medium text-text-secondary">Status</th>
                  <th className="py-3 px-4 w-20" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: perPage }).map((_, i) => (
                    <tr key={i} className="border-b border-border-primary">
                      <td className="px-4 py-3.5">
                        <div className="h-4 w-4 rounded" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex-shrink-0" />
                          <div className="space-y-1.5">
                            <div className="h-3.5 w-28 rounded" />
                            <div className="h-3 w-36 rounded" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5"><div className="h-3.5 w-20 rounded" /></td>
                      <td className="px-4 py-3.5"><div className="h-3.5 w-24 rounded" /></td>
                      <td className="px-4 py-3.5"><div className="h-5 w-14 rounded-full" /></td>
                      <td className="px-4 py-3.5" />
                    </tr>
                  ))
                ) : learners.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center body-base text-text-secondary">
                      No learners found
                    </td>
                  </tr>
                ) : (
                  learners.map((learner: Learner) => (
                    <tr key={learner.id} className="border-b border-border-primary hover:bg-background-secondary transition-colors">
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(learner.id)}
                          onChange={() => toggleSelect(learner.id)}
                          className="rounded border-border-primary"
                        />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          {learner.avatar_url ? (
                            <img
                              src={learner.avatar_url}
                              alt={learner.full_name}
                              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${getAvatarColor(learner.full_name)}`}>
                              {getInitials(learner.full_name)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <Link
                              to={`/learners/${learner.id}`}
                              className="body-small font-semibold text-text-primary hover:text-primary-600 dark:hover:text-primary-350 block truncate max-w-40"
                            >
                              {learner.full_name}
                            </Link>
                            <p className="text-xs text-text-secondary truncate max-w-40">{learner.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 body-small text-text-secondary py-3.5">
                        {learner.department_name || '—'}
                      </td>
                      <td className="px-4 body-small text-text-secondary py-3.5">
                        {learner.role_name || '—'}
                      </td>
                      <td className="px-4 body-small text-text-secondary py-3.5">
                        {parseApiDate(learner.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td
                        className="px-4 body-small text-text-secondary py-3.5 whitespace-nowrap"
                        title={lastLoginTitle(learner.last_login_at)}
                      >
                        {formatLastLogin(learner.last_login_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2.5 py-0.5 rounded body-small font-medium ${
                          learner.is_active
                            ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-400'
                            : 'bg-grey-100 text-grey-500 dark:bg-grey-800 dark:text-grey-400'
                        }`}>
                          {learner.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => setEditLearnerId(learner.id)}
                          className="p-1.5 hover:bg-background-tertiary rounded text-text-secondary hover:text-text-primary transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </phantom-ui>

          {/* Footer: Pagination */}
          <div className="flex items-center justify-end pt-1">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="body-small text-text-secondary">Rows per page:</span>
                <div className="relative">
                  <select
                    value={perPage}
                    onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="appearance-none pl-2 pr-6 py-1 border border-border-primary rounded bg-background-primary text-sm text-text-primary focus:outline-none"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                  </select>
                  <svg className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-text-secondary pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {!isLoading && total > 0 && (
                <span className="body-small text-text-secondary">{pageStart}–{pageEnd} of {total}</span>
              )}
              <div className="flex items-center">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1 || isLoading}
                  className="p-1 hover:bg-background-secondary rounded text-text-secondary hover:text-text-primary disabled:opacity-40 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages || isLoading}
                  className="p-1 hover:bg-background-secondary rounded text-text-secondary hover:text-text-primary disabled:opacity-40 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Open Selected Profile */}
      <div className="flex justify-center pb-2">
        <button
          className={`btn-primary px-10 py-3 shadow-lg transition-opacity ${selectedIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={selectedIds.length === 0}
          onClick={() => selectedIds.length > 0 && navigate(`/learners/${selectedIds[0]}`)}
        >
          Open Selected Profile
        </button>
      </div>

      {editLearnerId && (
        <LearnerEditModal
          learnerId={editLearnerId}
          onClose={() => setEditLearnerId(null)}
          onSaved={() => setEditLearnerId(null)}
          onDeleted={() => setEditLearnerId(null)}
        />
      )}
    </motion.div>
  );
};

export default LearnersListPage;
